import React, { useEffect, Fragment, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { showToast } from "../../../components/ToastifyNotification";
import Swal from "sweetalert2";
import Breadcrumb from "../../../components/common/breadcrumb";

// Excel & PDF
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { GenderWiseOpdRegistrationReportApi } from "../../../api/opd/opdReportsAPI";

const GenderWiseOpdRegistrationReport = () => {
	const [data, setData] = useState([]);
	const [filteredData, setFilteredData] = useState([]);

	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");

	const navigate = useNavigate();
	const dispatch = useDispatch();

	/** 📌 GROUP WITH ALL DATES, EVEN EMPTY **/
	const groupDataByDate = (data, fromDate, toDate) => {
		const start = new Date(fromDate);
		const end = new Date(toDate);

		// Create empty date-wise table first
		const result = {};
		while (start <= end) {
			const dateStr = start.toLocaleDateString("en-IN");
			result[dateStr] = {
				date: dateStr,
				total: 0,
				male: 0,
				female: 0,
				maleChild: 0,
				femaleChild: 0,
				newTotal: 0,
				oldTotal: 0,
				newMale: 0,
				newFemale: 0,
				newMaleChild: 0,
				newFemaleChild: 0,
				oldMale: 0,
				oldFemale: 0,
				oldMaleChild: 0,
				oldFemaleChild: 0,
			};
			start.setDate(start.getDate() + 1);
		}

		// Fill data if exists on that date
		data.forEach((item) => {
			const date = item.rawDate ? new Date(item.rawDate).toLocaleDateString("en-IN") : "";
			console.log(date);
			console.log(result);

			if (!date || !result[date]) return;

			const row = result[date];
			const sex = item.sex?.toLowerCase();
			const isChild = item.ageValue < 12 || item.ageSuffix === "Months" || item.ageSuffix === "Days";
			const isNew = item.status === "New";

			row.total++;

			if (sex === "male") {
				isChild ? row.maleChild++ : row.male++;
			} else if (sex === "female") {
				isChild ? row.femaleChild++ : row.female++;
			}

			if (isNew) {
				row.newTotal++;
				if (sex === "male") isChild ? row.newMaleChild++ : row.newMale++;
				if (sex === "female") isChild ? row.newFemaleChild++ : row.newFemale++;
			} else {
				row.oldTotal++;
				if (sex === "male") isChild ? row.oldMaleChild++ : row.oldMale++;
				if (sex === "female") isChild ? row.oldFemaleChild++ : row.oldFemale++;
			}
		});

		return Object.values(result);
	};

	/** 📌 Fetch Data **/
	const fetchData = async () => {
		dispatch({ type: "LOADER_START" });
		try {
			const requestData = { fromDate, toDate };

			const response = await GenderWiseOpdRegistrationReportApi(requestData);

			if (response.success) {
				showToast("success", response.message);

				const formattedData = response.data.map((item) => ({
					rawDate: item.date,
					patientName: item.patientName,
					sex: item.sex,
					ageSuffix: item.ageSuffix || "",
					ageValue: item.ageValue || 0,
					status: item.oldPatient === "Yes" ? "Old" : "New",
				}));

				setData(formattedData);
				setFilteredData(groupDataByDate(formattedData, fromDate, toDate));
			} else showToast("error", response.message);
		} catch (error) {
			showToast("error", error.message);
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

	const handleSearch = () => {
		if (!fromDate || !toDate) return showToast("error", "Please select date range");
		fetchData();
	};

	const resetFilters = () => {
		setFromDate("");
		setToDate("");
		setData([]);
		setFilteredData([]);
	};

	/** 📌 Table Columns **/
	const columns = [
		{ name: "Date", selector: row => row.date, sortable: true,width: "10%" },
		{ name: "Total", selector: row => row.total,width: "10%" },
		{ name: "Male", selector: row => row.male,width: "10%" },
		{ name: "Female", selector: row => row.female,width: "10%" },
		{ name: "Male Child", selector: row => row.maleChild,width: "10%" },
		{ name: "Female Child", selector: row => row.femaleChild,width: "10%" },
		{ name: "New Total", selector: row => row.newTotal,width: "10%" },
		{ name: "Old Total", selector: row => row.oldTotal,width: "10%" },
		{ name: "New Male", selector: row => row.newMale,width: "10%" },
		{ name: "New Female", selector: row => row.newFemale,width: "10%" },
		{ name: "New M. Child", selector: row => row.newMaleChild,width: "10%" },
		{ name: "New F. Child", selector: row => row.newFemaleChild,width: "10%" },
		{ name: "Old Male", selector: row => row.oldMale,width: "10%" },
		{ name: "Old Female", selector: row => row.oldFemale,width: "10%" },
		{ name: "Old M. Child", selector: row => row.oldMaleChild,width: "10%" },
		{ name: "Old F. Child", selector: row => row.oldFemaleChild,width: "10%" },
	];

	/** 📌 Export Excel **/
	const exportToExcel = () => {
		const sheetData = filteredData.map(row => ({
			Date: row.date,
			Total: row.total,
			Male: row.male,
			Female: row.female,
			"Male Child": row.maleChild,
			"Female Child": row.femaleChild,
			"New Total": row.newTotal,
			"Old Total": row.oldTotal,
			"New Male": row.newMale,
			"New Female": row.newFemale,
			"New Male Child": row.newMaleChild,
			"New Female Child": row.newFemaleChild,
			"Old Male": row.oldMale,
			"Old Female": row.oldFemale,
			"Old Male Child": row.oldMaleChild,
			"Old Female Child": row.oldFemaleChild,
		}));

		const worksheet = XLSX.utils.json_to_sheet(sheetData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Gender Wise Report");
		XLSX.writeFile(workbook, "Gender_Wise_OPD_Report.xlsx");
	};

	/** 📌 Export PDF **/
	const exportToPDF = () => {
		const doc = new jsPDF();
		doc.text("Gender Wise OPD Registration Report", 14, 10);

		autoTable(doc, {
			startY: 20,
			head: [columns.map(col => col.name)],
			body: filteredData.map(row => columns.map(col => row[col.selector.name] || "")),
		});

		doc.save("Gender_Wise_OPD_Report.pdf");
	};

	return (
		<Fragment>
			<Breadcrumb parent="OPD" title="Gender Wise OPD Registration Report" />

			<div className="container-fluid">
				<div className="row">
					<div className="col-sm-12">
						<div className="card">

							<div className="card-header">
								<h5>Gender Wise OPD Registration Report</h5>

								<div className="row mt-3">
									<div className="col-sm-2">
										<label>From Date</label>
										<input
											className="form-control"
											type="date"
											value={fromDate}
											onChange={(e) => setFromDate(e.target.value)}
										/>
									</div>

									<div className="col-sm-2">
										<label>To Date</label>
										<input
											className="form-control"
											type="date"
											value={toDate}
											onChange={(e) => setToDate(e.target.value)}
										/>
									</div>

									<div className="col-sm mt-4">
										<button className="btn btn-primary me-2" onClick={handleSearch}>
											Search
										</button>
										<button className="btn btn-warning" onClick={resetFilters}>
											Reset
										</button>
									</div>

									<div className="col-sm text-end mt-4">
										<button className="btn btn-success me-2" onClick={exportToExcel}>
											Excel
										</button>
										<button className="btn btn-danger" onClick={exportToPDF}>
											PDF
										</button>
									</div>
								</div>
							</div>

							<div className="card-body">
								<DataTable
									columns={columns}
									data={filteredData}
									pagination
									highlightOnHover
								/>
							</div>

						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default GenderWiseOpdRegistrationReport;
